import React, { useState, useCallback, useEffect, useRef } from 'react';
import { GoogleMap, Marker, Autocomplete, Rectangle } from '@react-google-maps/api';

const mapContainerStyle = {
  width: '100%',
  height: '400px',
  borderRadius: '12px',
  touchAction: 'none'
};

const defaultCenter = {
  lat: 23.8103, // Dhaka, Bangladesh
  lng: 90.4125
};

export interface ServiceArea {
  id: string; // unique internal id for list management
  name: string;
  address: string;
  lat: number;
  lng: number;
  bounds?: google.maps.LatLngBoundsLiteral; // Viewport bounds
}

interface ServiceAreaPickerProps {
  onAreasChange: (areas: ServiceArea[]) => void;
  selectedAreas?: ServiceArea[];
  readOnly?: boolean;
}

const ServiceAreaPicker: React.FC<ServiceAreaPickerProps> = ({
  onAreasChange,
  selectedAreas = [],
  readOnly = false
}) => {
  const [map, setMap] = useState<google.maps.Map | null>(null);
  // Controlled component: No local state for areas
  const [autocomplete, setAutocomplete] = useState<google.maps.places.Autocomplete | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [currentLocation, setCurrentLocation] = useState<{lat: number, lng: number} | null>(null);

  // Fit bounds when selectedAreas change
  useEffect(() => {
    if (map && selectedAreas.length > 0 && window.google) {
      const bounds = new window.google.maps.LatLngBounds();
      selectedAreas.forEach(area => {
        if (area.bounds) {
          bounds.union(area.bounds);
        } else {
          bounds.extend({ lat: area.lat, lng: area.lng });
        }
      });
      map.fitBounds(bounds);
      
      if (selectedAreas.length === 1 && !selectedAreas[0].bounds) {
         const listener = google.maps.event.addListener(map, "idle", () => { 
            map.setZoom(14); 
            google.maps.event.removeListener(listener); 
        });
      }
    } else if (map && selectedAreas.length === 0 && currentLocation) {
      map.panTo(currentLocation);
      map.setZoom(14);
    } else if (map && selectedAreas.length === 0) {
        map.setCenter(defaultCenter);
        map.setZoom(12);
    }
  }, [selectedAreas, map, currentLocation]);

  const onLoad = useCallback((map: google.maps.Map) => {
    setMap(map);
    // try get current location for display on load
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCurrentLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        }
      );
    }
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  const onAutocompleteLoad = (autocomplete: google.maps.places.Autocomplete) => {
    setAutocomplete(autocomplete);
  };

  const onPlaceChanged = () => {
    if (autocomplete) {
      const place = autocomplete.getPlace();
      if (place.geometry && place.geometry.location) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        const name = place.name || place.formatted_address || "Selected Location";
        const address = place.formatted_address || name;
        
        let bounds: google.maps.LatLngBoundsLiteral | undefined;
        if (place.geometry.viewport) {
            bounds = {
                north: place.geometry.viewport.getNorthEast().lat(),
                south: place.geometry.viewport.getSouthWest().lat(),
                east: place.geometry.viewport.getNorthEast().lng(),
                west: place.geometry.viewport.getSouthWest().lng(),
            };
        } else {
           // fallback box
           bounds = {
              north: lat + 0.01,
              south: lat - 0.01,
              east: lng + 0.01,
              west: lng - 0.01
           };
        }

        addArea({
            id: Date.now().toString(),
            name,
            address,
            lat,
            lng,
            bounds
        });
        setSearchValue('');
      }
    }
  };

  const addArea = (newArea: ServiceArea) => {
    onAreasChange([...selectedAreas, newArea]);
  };

  const removeArea = (id: string) => {
    onAreasChange(selectedAreas.filter(a => a.id !== id));
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          setCurrentLocation({ lat, lng });
          
          // Reverse geocode
          const geocoder = new google.maps.Geocoder();
          geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === 'OK' && results && results[0]) {
              const place = results[0];
               let name = "My Current Location";
               const locality = place.address_components.find(c => c.types.includes('locality'))?.long_name;
               const sublocality = place.address_components.find(c => c.types.includes('sublocality'))?.long_name;
               if (sublocality) name = sublocality;
               else if (locality) name = locality;

              let bounds: google.maps.LatLngBoundsLiteral | undefined;
              if (place.geometry.viewport) {
                 bounds = {
                    north: place.geometry.viewport.getNorthEast().lat(),
                    south: place.geometry.viewport.getSouthWest().lat(),
                    east: place.geometry.viewport.getNorthEast().lng(),
                    west: place.geometry.viewport.getSouthWest().lng(),
                 };
              } else {
                 bounds = {
                    north: lat + 0.01,
                    south: lat - 0.01,
                    east: lng + 0.01,
                    west: lng - 0.01
                 };
              }

              addArea({
                id: Date.now().toString(),
                name: name,
                address: place.formatted_address,
                lat,
                lng,
                bounds
              });
            } else {
               addArea({
                id: Date.now().toString(),
                name: "My GPS Location",
                address: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
                lat,
                lng,
                bounds: {
                    north: lat + 0.01,
                    south: lat - 0.01,
                    east: lng + 0.01,
                    west: lng - 0.01
                }
              });
            }
          });
        },
        (error) => {
          console.error("Geolocation error:", error);
          alert("Could not get current location.");
        }
      );
    }
  };

  return (
    <div className="space-y-4">
      {!readOnly && (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800 animate-fade-in">
        <div className="flex gap-3">
          <svg className="w-6 h-6 flex-shrink-0 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
          </svg>
          <div>
            <h4 className="font-semibold text-blue-900 mb-1">Define Your Service Areas</h4>
            <p>
              Add specific areas (like neighborhoods or cities) where you are willing to work. 
              Search and select multiple locations to create your coverage map.
            </p>
          </div>
        </div>
      </div>
      )}

      {!readOnly && (
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="flex-1">
          <Autocomplete
            onLoad={onAutocompleteLoad}
            onPlaceChanged={onPlaceChanged}
            options={{
              componentRestrictions: { country: 'bd' },
              fields: ['geometry', 'name', 'formatted_address', 'address_components']
            }}
          >
            <div className="relative">
                <input
                    type="text"
                    placeholder="Add area (e.g. Dhanmondi, Uttara)..."
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-shadow shadow-sm hover:shadow-md"
                />
                 <div className="absolute left-3 top-3.5 text-gray-400">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
                 </div>
            </div>
          </Autocomplete>
        </div>
        <button
          type="button"
          onClick={getCurrentLocation}
          className="px-4 py-3 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 hover:border-primary-300 hover:text-primary-600 transition-all flex items-center justify-center gap-2 whitespace-nowrap shadow-sm group"
        >
          <svg className="w-5 h-5 text-gray-400 group-hover:text-primary-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          Current Location
        </button>
      </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2 rounded-xl overflow-hidden border border-gray-200 shadow-sm">
             <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={defaultCenter}
                zoom={12}
                onLoad={onLoad}
                onUnmount={onUnmount}
                options={{
                streetViewControl: false,
                mapTypeControl: false,
                fullscreenControl: true,
                }}
            >
                {/* Show marker for current user location if available */}
                {currentLocation && (
                  <Marker
                    position={currentLocation}
                    icon={{
                      path: google.maps.SymbolPath.CIRCLE,
                      scale: 6,
                      fillColor: "#4285F4",
                      fillOpacity: 1,
                      strokeColor: "#ffffff",
                      strokeWeight: 2,
                    }}
                    title="My Location"
                  />
                )}

                {selectedAreas.map(area => (
                  <React.Fragment key={area.id}>
                    {/* Only show Polygon/Rectangle, no pin */}
                    {area.bounds && (
                    <Rectangle
                      bounds={area.bounds}
                      options={{
                        strokeColor: "#e03761", // Brand Color
                        strokeOpacity: 0.8,
                        strokeWeight: 2,
                        fillColor: "#e03761",
                        fillOpacity: 0.15, // Slightly transparent fill
                        clickable: false,
                        draggable: false,
                        editable: false,
                      }}
                    />
                    )}
                  </React.Fragment>
                ))}
            </GoogleMap>
          </div>

          <div className="md:col-span-1 bg-gray-50 rounded-xl p-4 border border-gray-200 h-fit max-h-[400px] overflow-y-auto">
             <h4 className="font-bold text-gray-800 mb-3 flex items-center justify-between">
                <span>Selected Areas</span>
                <span className="bg-primary-100 text-primary-700 text-xs px-2 py-0.5 rounded-full">{selectedAreas.length}</span>
             </h4>
             
             {selectedAreas.length === 0 ? (
                 <div className="text-center py-8 text-gray-400">
                     <svg className="w-10 h-10 mx-auto mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                     <p className="text-sm">No areas selected yet.</p>
                 </div>
             ) : (
                 <div className="space-y-3">
                     {selectedAreas.map(area => (
                         <div key={area.id} className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm relative group hover:border-red-200 transition-colors">
                             <h5 className="font-medium text-gray-800 text-sm">{area.name}</h5>
                             <p className="text-xs text-gray-500 truncate mt-0.5" title={area.address}>{area.address}</p>
                             {!readOnly && (
                             <button
                                onClick={() => removeArea(area.id)}
                                className="absolute top-2 right-2 text-gray-300 hover:text-red-500 transition-colors p-1"
                             >
                                 <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"></path></svg>
                             </button>
                             )}
                         </div>
                     ))}
                 </div>
             )}
          </div>
      </div>
    </div>
  );
};

export default ServiceAreaPicker;
